// Angular
import {
  HttpClient,
  HttpContext,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

// RxJS
import { exhaustMap, Observable, switchMap, take } from 'rxjs';

// Data Contracts
import {
  JudgeReviewCurrentActionDocuments,
  JudgeReviewFiling,
  JudgeReviewFilingConfiguration,
  JudgeReviewSubmitResponse,
  JudgeReviewSummary,
} from '@data-contracts';

// Local
import { ENV_CONFIG } from '@app/core/app-config/app-config.service';
import { BYPASS_TOKEN_INJECTION } from '@app/core/fsx.interceptor';
import {
  IJudgeReviewEndpoints,
  judgeReviewEndpoints,
  withTrailingSlash,
} from '@app/types/api/endpoints/judge-review.endpoints';
import { EnvConfig } from '@app/types/environments';
import { JsonPatchOperation } from '@app/services/utilities/patch-helpers.service';

/**
 * A service containing the JudgeReview API endpoints.
 *
 * NOTE on operators: the `exhaustMap` / `switchMap` used below only bridges
 * config → request and provides NO duplicate-request protection (each call
 * builds a fresh pipe). Guarding against duplicate submits is the caller's
 * responsibility — the studio/confirm/detail components disable their
 * submit/proceed buttons while `busy()` is true.
 */
@Injectable({ providedIn: 'root' })
export class JudgeReviewApiService {
  /**
   * The Angular HttpClient service used to make the API calls.
   */
  private readonly http = inject(HttpClient);

  /**
   * The injected environment configuration used to set the base URL for the API calls.
   */
  private readonly envConfigSource = inject<Observable<EnvConfig>>(ENV_CONFIG);

  /**
   * Config gate for every request below.
   *
   * `ENV_CONFIG` is a BehaviorSubject that never completes, so piping it
   * straight into `switchMap` yields a request observable that emits its
   * response but never completes. That silently breaks every
   * completion-dependent operator downstream — `forkJoin` never emits,
   * `finalize` / `complete` handlers never run — and a later config emission
   * would re-fire the request (a duplicate POST/PATCH). `take(1)` closes the
   * config gate after the first value so each request completes with its
   * response.
   */
  private readonly envConfig = this.envConfigSource.pipe(take(1));

  /** The Judge Review endpoint URLs for the configured API server. */
  private urls(envConfig: EnvConfig): IJudgeReviewEndpoints {
    return judgeReviewEndpoints(withTrailingSlash(envConfig.ApiServer.BaseURL));
  }

  /**
   * A public api method to allow for the retrieval of the JudgeReviewSummary object.
   *
   * Returns a single wrapper object whose `availableJudgeUsers` list contains one
   * entry per judge the requesting user can act on behalf of — each with the
   * judge's uniqueId, name, court, and their pending filing summaries.
   *
   * @returns The JudgeReviewSummary wrapper object.
   */
  getJudgeReviewItemSummaries(): Observable<JudgeReviewSummary> {
    return this.envConfig.pipe(
      switchMap((envConfig: EnvConfig) => {
        return this.http.get<JudgeReviewSummary>(
          this.urls(envConfig).summaries(),
        );
      }),
    );
  }

  /**
   * A public api method to get a Judge Review Filing object.
   *
   * @param filingId The filing unique id (GUID) to retrieve the Judge Review Filing object for.
   *
   * @returns The Judge Review Filing object for the given filingId.
   */
  getJudgeReviewFiling(filingId: string): Observable<JudgeReviewFiling> {
    return this.envConfig.pipe(
      switchMap((envConfig: EnvConfig) => {
        return this.http.get<JudgeReviewFiling>(
          this.urls(envConfig).filing(filingId),
        );
      }),
    );
  }

  /**
   * A public api method to reset a Judge Review Filing object to its initial state.
   *
   * @param filingId The filing unique id (GUID) to reset the Judge Review Filing object for.
   *
   * @returns The Judge Review Filing object for the given filingId in the original state.
   */
  resetJudgeReviewFiling(filingId: string): Observable<JudgeReviewFiling> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) => {
        return this.http.post<JudgeReviewFiling>(
          this.urls(envConfig).filing(filingId),
          null,
        );
      }),
    );
  }

  /**
   * Sets the documents of the in-progress action (1, many or all of the documents still
   * pending), creating the action when none is in progress.
   *
   * The server runs copy-on-open for the chosen documents: it creates each document's
   * working copy in a fresh DocStore collection and stamps
   * `currentAction.documents[].judgeReviewDocumentStoreId` (the id every studio call needs)
   * and `currentAction.collectionId`. Call again with a different list to resize the action.
   *
   * @param filingId The reviewedFilingUniqueId (GUID) of the filing.
   * @param existingDocumentIds The integer ids of the pending documents to act on together.
   *
   * @returns The Judge Review Filing with `currentAction` populated.
   */
  putJudgeReviewCurrentActionDocuments(
    filingId: string,
    existingDocumentIds: number[],
  ): Observable<JudgeReviewFiling> {
    const body: JudgeReviewCurrentActionDocuments = { existingDocumentIds };
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) =>
        this.http.put<JudgeReviewFiling>(
          this.urls(envConfig).currentActionDocuments(filingId),
          body,
        ),
      ),
    );
  }

  /**
   * A public api method to patch a Judge Review Filing Object.
   *
   * @param filingUniqueId The filingUniqueId (GUID) to retrieve the Judge Review Data object for.
   * @param patchData The patch data to be applied to the Judge Review Data object.
   *
   * @returns The Judge Review Data object after patching
   */
  patchJudgeReviewFiling(
    filingUniqueId: string,
    patchData: JsonPatchOperation[],
  ): Observable<JudgeReviewFiling> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) => {
        return this.http.patch<JudgeReviewFiling>(
          this.urls(envConfig).filing(filingUniqueId),
          patchData,
          {
            headers: new HttpHeaders({
              'Content-Type': 'application/json-patch+json',
            }),
          },
        );
      }),
    );
  }

  /**
   * A public api method to delete a Judge Review Filing original and modified state on the server.
   *
   * @param filingUniqueId The filingUniqueId (GUID) to retrieve the Judge Review Data object for.
   *
   * @returns The Judge Review Data object after patching
   */
  deleteJudgeReviewFiling(filingUniqueId: string): Observable<void> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) => {
        return this.http.delete<void>(
          this.urls(envConfig).filing(filingUniqueId),
        );
      }),
    );
  }

  /**
   * Deletes the judge's outgoing DocStore rendering for a single document and
   * reverts it to KeepOriginal. Idempotent — a no-op when no rendering exists.
   *
   * @param filingId The reviewedFilingUniqueId (GUID) of the filing.
   * @param existingDocumentId The integer existingDocumentId of the document to reset.
   *
   * @returns An observable that completes when the delete is accepted (200 OK) or
   *   the document had no rendering (404, treated as success by the caller).
   */
  deleteJudgeReviewDocumentRendering(
    filingId: string,
    existingDocumentId: number,
  ): Observable<void> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) => {
        return this.http.delete<void>(
          this.urls(envConfig).documentRendering(filingId, existingDocumentId),
        );
      }),
    );
  }

  /**
   * Surgically deletes a single named rendering (judge-review-replace or
   * judge-review-attach) from the working-copy document. Does not revert
   * JudgeDocumentOption or affect sibling renderings.
   *
   * @param filingId The reviewedFilingUniqueId (GUID) of the filing.
   * @param documentStoreId The judgeReviewDocumentStoreId (GUID) of the document.
   * @param renderingName The slot name to delete ('judge-review-replace' or 'judge-review-attach').
   */
  deleteJudgeReviewSingleDocumentRendering(
    filingId: string,
    documentStoreId: string,
    renderingName: string,
  ): Observable<void> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) =>
        this.http.delete<void>(
          this.urls(envConfig).singleRendering(
            filingId,
            documentStoreId,
            renderingName,
          ),
        ),
      ),
    );
  }

  /**
   * A public api method to validate and submit a Judge Review Filing Object.
   *
   * On success (200) the server returns a {@link JudgeReviewSubmitResponse} — the
   * authoritative receipt data (new filing id, Filed & Served timestamp, submitter /
   * authorizer, per-document dispositions). The pre-submit working copy is deleted
   * server-side on submit, so this response (not the filing) is what the Receipt
   * renders. When validation fails the server responds 400 with an ErrorResponse
   * body; the caller inspects the error to surface validationErrors.
   *
   * @param filingUniqueId The reviewedFilingUniqueId (GUID) of the filing to submit.
   *
   * @returns The judge review submit response.
   */
  submitJudgeReviewFiling(
    filingUniqueId: string,
  ): Observable<JudgeReviewSubmitResponse> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) => {
        return this.http.post<JudgeReviewSubmitResponse>(
          this.urls(envConfig).submit(filingUniqueId),
          null,
        );
      }),
    );
  }

  /**
   * A public api method to get a Judge Review Filing Configuration object.
   *
   * @param filingUniqueId The filingUniqueId (GUID) to retrieve the Judge Review Configuration object for.
   *
   * @returns The Judge Review Configuration object for the given filingUniqueId.
   */
  getJudgeReviewConfiguration(
    filingUniqueId: string,
  ): Observable<JudgeReviewFilingConfiguration> {
    return this.envConfig.pipe(
      switchMap((envConfig: EnvConfig) => {
        return this.http.get<JudgeReviewFilingConfiguration>(
          this.urls(envConfig).configuration(filingUniqueId),
        );
      }),
    );
  }

  /**
   * Uploads the raw file bytes directly to Azure Blob Storage using the pre-signed
   * SAS URI returned on JudgeReviewDocument.documentUploadUri in the preceding PATCH
   * response. The SAS URI already encodes credentials, so token injection is bypassed
   * and no Authorization header is sent. Mirrors the filing-draft document upload flow.
   *
   * @param uploadUri The 1-hour SAS URI from JudgeReviewDocument.documentUploadUri.
   * @param file The file to upload.
   *
   * @returns An observable that completes when the blob upload finishes.
   */
  uploadJudgeReviewDocumentToBlob(
    uploadUri: string,
    file: File,
  ): Observable<void> {
    return this.http.put<void>(uploadUri, file, {
      context: new HttpContext().set(BYPASS_TOKEN_INJECTION, true),
      headers: new HttpHeaders({ 'x-ms-blob-type': 'BlockBlob' }),
    });
  }

  /**
   * Commit step of the document upload flow. Call after the file bytes have been
   * PUT to the SAS URI. Notifies the API which DocStore slots were filled so the
   * worker can move the bytes in and trigger PDF rendering.
   *
   * @param filingId The reviewedFilingUniqueId (GUID) of the filing.
   * @param documentStoreIds The judgeReviewDocumentStoreId values whose bytes were just uploaded.
   * @param finalize When `false` (default), the worker produces the preview rendering
   *   (`judge-review-replace` / `judge-review-attach`). Pass `true` at Continue (FE part 3)
   *   to finalize and produce the `converted-pdf` rendering used for submit.
   *
   * @returns An observable that completes when the commit is accepted (200 OK).
   */
  commitJudgeReviewDocumentUpload(
    filingId: string,
    documentStoreIds: string[],
    finalize?: boolean,
  ): Observable<void> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) => {
        const params: Record<string, string> = {};
        if (finalize !== undefined) {
          params['finalize'] = String(finalize);
        }
        return this.http.post<void>(
          this.urls(envConfig).documentUpload(filingId),
          documentStoreIds,
          { params },
        );
      }),
    );
  }

  /**
   * Downloads the converted-pdf rendering of a judge's outgoing DocStore document
   * via the by-GUID judge-review rendering endpoint.
   *
   * @param filingId The reviewedFilingUniqueId (GUID) of the reviewed filing.
   *   Do NOT pass the judge's collection GUID here — the API resolves that internally.
   * @param documentStoreId The judgeReviewDocumentStoreId (GUID) of the outgoing document.
   * @param rendering The rendering type to retrieve (e.g. 'converted-pdf').
   *
   * @returns An observable of the raw ArrayBuffer for the rendering.
   */
  /**
   * Downloads the reviewing judge's banner (the configured overlay file, converted
   * to PDF when it was uploaded as RTF).
   *
   * Served by the API rather than read from the banner's Azure Files SAS URL: that
   * URL is a different origin, so a browser `fetch` of it is blocked without CORS
   * on the storage account, and the studio could neither preview the banner nor
   * burn it into the outgoing order. Responds 204 (empty body) when the judge has
   * no banner.
   *
   * The banner is a template: the API substitutes its `%Token%` placeholders —
   * `%JudicialAction%`, `%CaseNumber%`, `%PrimaryJudge%` and the rest — from this
   * document's data, so the document id and selected judicial action are part of
   * the request. Without them those tokens stamp `N/A`.
   *
   * @param filingId The reviewedFilingUniqueId (GUID) of the reviewed filing.
   * @param documentId The JCMS document the banner is for.
   * @param judicialActionId The judicial action the judge selected, when set.
   * @returns An observable of the raw banner bytes.
   */
  getJudgeReviewBanner(
    filingId: string,
    documentId?: number | null,
    judicialActionId?: string | null,
  ): Observable<ArrayBuffer> {
    return this.envConfig.pipe(
      switchMap((envConfig: EnvConfig) => {
        let params = new HttpParams();
        if (documentId != null) {
          params = params.set('documentId', String(documentId));
        }
        if (judicialActionId) {
          params = params.set('judicialActionId', judicialActionId);
        }
        return this.http.get(this.urls(envConfig).banner(filingId), {
          responseType: 'arraybuffer',
          params,
        });
      }),
    );
  }

  getJudgeReviewDocumentRendering(
    filingId: string,
    documentStoreId: string,
    rendering: string,
  ): Observable<ArrayBuffer> {
    return this.envConfig.pipe(
      switchMap((envConfig: EnvConfig) =>
        this.http.get(
          this.urls(envConfig).renderingDownload(
            filingId,
            documentStoreId,
            rendering,
          ),
          {
            responseType: 'arraybuffer',
            params: { fallbackToPdf: 'true' },
          },
        ),
      ),
    );
  }

  /**
   * Appends a blank page to the active rendering of the given working-copy document.
   *
   * The raw PDF bytes of a one-page blank document are sent as the request body.
   * The backend uploads the bytes to a temp blob, acquires the patch lock, reads the
   * active rendering, appends this page via the existing DocStore merge/append
   * pipeline, and sends a `ModernDocumentUploadMessage` — exactly like a Replace or
   * Attach upload. Readiness is signalled back via the existing SignalR
   * document-ready push, which triggers a `reloadTick` in the studio.
   *
   * @param filingId       The `reviewedFilingUniqueId` (GUID) of the reviewed filing.
   * @param documentStoreId The `judgeReviewDocumentStoreId` (GUID) of the document.
   * @param blankPdfBytes  The serialised one-page blank PDF to append.
   * @returns An observable that completes (200 OK) when the request is accepted.
   */
  addBlankPageToDocument(
    filingId: string,
    documentStoreId: string,
    blankPdfBytes: Uint8Array,
  ): Observable<void> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) =>
        this.http.post<void>(
          this.urls(envConfig).blankPage(filingId, documentStoreId),
          blankPdfBytes,
          {
            headers: new HttpHeaders({
              'Content-Type': 'application/octet-stream',
            }),
          },
        ),
      ),
    );
  }

  /**
   * Marks a single judge-review document as reviewed at the end of a successful Continue.
   *
   * The API re-checks that the document carries a non-Pending judicial action before it sets
   * the flag, so `isReviewed` on the returned filing is server-authoritative. A Pending or
   * unresolvable action comes back as 400 / `JRD018`.
   *
   * @param filingId           The `reviewedFilingUniqueId` (GUID) of the reviewed filing.
   * @param existingDocumentId The integer document id of the document just reviewed.
   * @returns The updated filing, so the caller can refresh state in one round trip.
   */
  markDocumentReviewed(
    filingId: string,
    existingDocumentId: number,
  ): Observable<JudgeReviewFiling> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) =>
        this.http.post<JudgeReviewFiling>(
          this.urls(envConfig).documentReviewed(filingId, existingDocumentId),
          null,
        ),
      ),
    );
  }
}
